import React, { ReactElement } from "react";
import { LIBERAL } from "../constants";
import IconFascist from "../assets/player-icon-fascist.png";
import IconLiberal from "../assets/player-icon-liberal.png";
import "./Prompts.css";

type PolicyCardArtProps = {
  /** LIBERAL or FASCIST. */
  policy: string;
  id?: string;
  className?: string;
  /**
   * Leave a margin of about 10% around the card, like the transparent border of the old card art.
   * The enacted-policy folder is laid out for that margin, so the card sits inside it.
   */
  inset?: boolean;
};

/**
 * A policy card drawn as vector art: a deep gradient card with a fine inner frame, the party emblem
 * (dove or skull) in a medallion, the party name and a few lines of "text". The shape is 576 x 772
 * like the card art it replaces, so it fits the same layouts; its size follows the CSS height.
 */
export default function PolicyCardArt(props: PolicyCardArtProps): ReactElement {
  const liberal = props.policy === LIBERAL;
  const key = liberal ? "lib" : "fas";
  const [top, bottom, glow] = liberal
    ? ["#4a95dc", "#1b4a82", "#9fd0ff"]
    : ["#ee6244", "#8c2013", "#ffb59f"];
  return (
    <svg
      id={props.id}
      className={"policy-art" + (props.className ? " " + props.className : "")}
      viewBox={props.inset ? "-32 -43 640 858" : "0 0 576 772"}
      role="img"
      aria-label={
        (liberal ? "Liberal" : "Fascist") +
        " policy card with a " +
        (liberal ? "dove" : "skull") +
        " emblem"
      }
    >
      <defs>
        <linearGradient id={"pa-bg-" + key} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor={top} />
          <stop offset="1" stopColor={bottom} />
        </linearGradient>
        <radialGradient id={"pa-glow-" + key} cx="0.5" cy="0.4" r="0.6">
          <stop offset="0" stopColor={glow} stopOpacity="0.55" />
          <stop offset="1" stopColor={glow} stopOpacity="0" />
        </radialGradient>
        <clipPath id={"pa-clip-" + key}>
          <rect width="576" height="772" rx="46" />
        </clipPath>
      </defs>
      <rect width="576" height="772" rx="46" fill={"url(#pa-bg-" + key + ")"} />
      <g clipPath={"url(#pa-clip-" + key + ")"}>
        <rect width="576" height="772" fill={"url(#pa-glow-" + key + ")"} />
        {/* soft diagonal sheen */}
        <path d="M-40 520L616 120V260L-40 700Z" fill="#fff" opacity="0.06" />
        <rect y="720" width="576" height="52" fill="#000" opacity="0.18" />
      </g>
      <rect x="24" y="24" width="528" height="724" rx="30" fill="none" stroke="#fff" strokeOpacity="0.4" strokeWidth="3" />
      <rect x="38" y="38" width="500" height="696" rx="20" fill="none" stroke="#fff" strokeOpacity="0.16" strokeWidth="2" />

      <circle cx="288" cy="268" r="128" fill="#000" opacity="0.22" />
      <circle cx="288" cy="268" r="128" fill="none" stroke="#fff" strokeOpacity="0.55" strokeWidth="4" />
      <circle cx="288" cy="268" r="112" fill="none" stroke="#fff" strokeOpacity="0.18" strokeWidth="2" strokeDasharray="3 9" strokeLinecap="round" />
      <image
        className="policy-art-emblem"
        href={liberal ? IconLiberal : IconFascist}
        x="198"
        y="178"
        width="180"
        height="180"
      />

      <text className="policy-art-title" x="288" y="506" textAnchor="middle" fontSize="96" fill="#fff" textLength="400" lengthAdjust="spacingAndGlyphs">
        {liberal ? "LIBERAL" : "FASCIST"}
      </text>
      <path d="M96 542H266M310 542H480" stroke="#fff" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round" />
      <path d="M288 532L298 542L288 552L278 542Z" fill="#fff" opacity="0.85" />
      <text className="policy-art-sub" x="288" y="598" textAnchor="middle" fontSize="30" fill="#fff" fillOpacity="0.85" letterSpacing="9">
        POLICY
      </text>
      <g fill="#fff" opacity="0.22">
        <rect x="96" y="634" width="384" height="9" rx="4.5" />
        <rect x="96" y="662" width="384" height="9" rx="4.5" />
        <rect x="96" y="690" width="250" height="9" rx="4.5" />
      </g>
    </svg>
  );
}
